import { useEffect, useState } from 'react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useAuthContext } from '@contexts/authContext'
import axios from '@utils/webRequests'
import { colors, fontSize, size } from '@styles/theme'
import styled from '@emotion/styled'
import { SheerFormTextField } from '@elements/Inputs'
import StyledAlert from '@elements/StyledAlert'
import { Button, ButtonBase } from '@mui/material'
import { ActionButton, BottomButtonGroup, ButtonGroupClearFix } from '@components/auth/landing/LandingElements'
import TermsAndconditionsModal from '@components/legal/TermsAndConditionsModal'
import useDialog from '@hooks/useDialog'

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${size(1)};
`

const cardElementOptions = {
  style: {
    base: {
      color: 'white',
      fontSize: '16px'
    },
  }
}

const CardElementContainerOuter = styled.div`
  background-color: rgba(255,255,255,0.1);
  border-radius: 15px;
  border: 1px solid grey;
`

const CardElementContainerInner = styled.div`
  height: 100%;
  width: 100%;
  padding: ${size(2.15)};
  border-radius: 14px;
  border: 1px solid transparent;
`

const SubscriptionDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 ${size(2)};
`

const DetailsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  line-height: ${size(3)};
  font-weight: 350;
`

const BoldDetail = styled.b`
  font-weight: 500;
`

const TermsButton = styled(ButtonBase)`
  margin: ${size(5)} auto;
`

const SubscriptionConfirmationForm = ({ clientSecret, subscription, isLoading, setIsLoading, setIsPaymentSuccessful }) => {
  const [isTermsOpen, openTerms, closeTerms] = useDialog('subscription_confirmation_terms', 'subscription-confirmation-terms-open')
  const { state: authState, handleInstantUpdate } = useAuthContext()
  const [cardBorderEl, setCardBorderEl] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [name, setName] = useState('')
  const elements = useElements()
  const stripe = useStripe()

  useEffect(() => {
    if (!elements || !cardBorderEl) return;
    const cardElement = elements.getElement('card')
    if (cardElement) {
      // adds border emphasis on outline focus
      cardElement.on('focus', () => {
        cardBorderEl.style.border = '1px solid grey'
      })
      // removes card border when unfocused
      cardElement.on('blur', () => {
        cardBorderEl.style.border = '1px solid transparent'
      })
      // cleans up handlers
      return () => cardElement.removeAllListeners()
    }
  }, [elements, cardBorderEl])

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;
    const cardElement = elements.getElement('card')
    // NOTE: Make sure to disable form submission until Stripe.js has loaded.
    if (!stripe || !elements || !cardElement) return;

    // Setup data for request
    const data = {
      payment_method: {
        card: cardElement,
        billing_details: { name, },
      },
    }

    // Sends request to stripe for payment
    // WARNING: the reponse from the confirmPaymentMethod WILL NOT be caught as an error in a promise, hence using async await
    setIsLoading(true)
    const res = await stripe.confirmCardPayment(clientSecret, data)
    if (res.error) {
      setIsLoading(false)
      setErrorMessage(res?.error?.message)
      console.error(res.error);
    } else {
      axios.get(`/api/billing/customers`)
        .then((res) => {
          setIsLoading(false)
          setErrorMessage(null)
          setIsPaymentSuccessful(true)
          if (res.data.is_subscribed) {
            handleInstantUpdate({ ...authState?.user, isSubscribed: true })
          }
        })
    }
  }

  const renderErrorMessage = () => {
    if (errorMessage) return (
      <StyledAlert
        AlertStyles={{ marginBottom: size(5)}}
        kind='error'>
        {errorMessage}
      </StyledAlert>
    )
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <TermsAndconditionsModal
        isOpen={isTermsOpen}
        setClosed={closeTerms}
      />
      {renderErrorMessage()}
      <SheerFormTextField
        required
        name='name'
        label='Full Name'
        placeholder='Your name as it appears on card'
        onChange={(e) => setName(e.value)}
        value={name}
      />
      <CardElementContainerOuter >
        <CardElementContainerInner ref={node => node && setCardBorderEl(node)}>
          <CardElement options={cardElementOptions} />
        </CardElementContainerInner>
      </CardElementContainerOuter>
      <br />
      <br />
      <SubscriptionDetails>
        <DetailsSection>
          Subtotal
          <span>
            <BoldDetail>
              ${(subscription?.subtotal/subscription.intervalCount/100)?.toFixed(2)}
            </BoldDetail>
            /{subscription.interval}
          </span>
        </DetailsSection>
        {subscription.tax &&
          <DetailsSection>
            Taxes
            <span>${(subscription?.tax/100)?.toFixed(2)}</span>
          </DetailsSection>
        }
        <br />
        <DetailsSection>
          Duration
          <span>{subscription.duration}</span>
        </DetailsSection>
        <DetailsSection>
          Total
          <span>
            <BoldDetail>
              ${(subscription?.total/100)?.toFixed(2)}
            </BoldDetail>
          </span>
        </DetailsSection>
        <DetailsSection>
          <TermsButton onClick={() => openTerms()}>
            View our&nbsp;<u>Terms and Conditions</u>
          </TermsButton>
        </DetailsSection>
      </SubscriptionDetails>
      <br />
      <br />
      <ButtonGroupClearFix />
      <BottomButtonGroup style={{ left: 0 }}>
        <ActionButton
          style={{ background: colors.statusColors.right_now('primary') }}
          type='submit'>
          {isLoading ? 'Confirming Payment...' : 'Submit'}
        </ActionButton>
      </BottomButtonGroup>
    </FormContainer>
  )
}

export default SubscriptionConfirmationForm
